import { memo, useEffect, useContext, useCallback } from "react";
import { Button, Text, ScrollArea } from "@mantine/core";
import { MdDownload } from "react-icons/md";
import DashboardCard from "../../DashboardCard";
import { DEFAULT_ORDER, ROUTES, ROUTE_NAMES } from "src/utils/constants";
import { AppContext } from "src/api/AppContext";
import {
  GetPosReportPageDocument,
  GetPosReportPageQuery,
  GetPosReportPageQueryVariables,
  ProductType,
  useDownloadPoStCsvMutation,
} from "src/generated/graphql";
import { ArrayElement } from "src/utils/helperTypes";
import { FetchParams, OrderBy, QueryResponse } from "@kapex/tables";
import { useApolloClient } from "@apollo/client";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import { downloadCSV, getFileNameCsv } from "src/utils/utils";
import { useLocation } from "react-router";
import { useDashboardContext } from "src/api/context/DashboardContext";

export type POSReportData = ArrayElement<
  GetPosReportPageQuery["posReportPage"]["items"]
>;

const POSReport = () => {
  const { pathname } = useLocation();
  const {
    filters,
    setFilters,
    getDateRangeText,
    handleModal,
    handleLocChange,
    paginationParams,
    setPaginationParams,
  } = useDashboardContext();
  const { query } = useApolloClient();
  const { setCurrentPageTitle } = useContext(AppContext);

  const [downloadPOSCsv, { loading: isCsvDownloading }] =
    useDownloadPoStCsvMutation();

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.REPORT_POS);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleSaveAsCSV = async () => {
    if (!filters.dateRange?.[0]) return;
    const { dateRange, locations } = filters;
    const { data } = await downloadPOSCsv({
      variables: {
        data: {
          startAt: dateRange[0],
          endAt: dateRange[1],
          locations: locations.map((e) => ({ id: e })),
        },
        pagination: paginationParams,
      },
    });

    downloadCSV(
      data?.downloadPosCSV,
      getFileNameCsv(
        pathname === ROUTES.REPORT_PACKAGE ? "Package_Report" : "POS_Report",
        filters.dateRange
      )
    );
  };

  const getReportPage = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<POSReportData>> => {
    const fetchParamsUpdate = fetchParams;

    if (pathname === ROUTES.REPORT_PACKAGE) {
      const hasProductTypeFilter = fetchParamsUpdate.filters?.some(
        (filter) => filter.field === "productType"
      );
      if (!hasProductTypeFilter) {
        fetchParamsUpdate.filters!.push({
          type: "select",
          field: "productType",
          values: ["Package"],
        });
      }
    }

    const { dateRange, locations } = filters;
    if (!dateRange?.[0])
      return {
        rowData: [],
        rowCount: 0,
      };

    setPaginationParams({
      orderBy:
        fetchParamsUpdate.orderBy || (DEFAULT_ORDER.client_name as OrderBy),
      filters: fetchParamsUpdate.filters,
    });

    let { data } = await query<
      GetPosReportPageQuery,
      GetPosReportPageQueryVariables
    >({
      query: GetPosReportPageDocument,
      variables: {
        pagination: {
          count: fetchParamsUpdate.count,
          page: fetchParamsUpdate.page,
          orderBy: fetchParamsUpdate.orderBy || DEFAULT_ORDER.client_name,
          filters: fetchParamsUpdate.filters,
        },
        data: {
          startAt: dateRange[0],
          endAt: dateRange[1],
          locations: locations.map((e) => ({ id: e })),
        },
      },
    });

    if (data.posReportPage) {
      return {
        rowCount: data.posReportPage.totalRows,
        rowData: data.posReportPage.items as POSReportData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  const ShowTable = useCallback(() => {
    if (!filters.dateRange?.[0]) return <></>;
    return (
      <ScrollableDataGrid<POSReportData>
        topMargin={122}
        columnConfigs={[
          {
            field: "chargedTo_id",
            name: "Account Id",
            filter: {
              field: "chargedTo.id",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.chargedTo?.id || "N/A"}
              </Text>
            ),
          },
          {
            field: "client_name",
            name: "Account Name",
            filter: {
              field: "client_name",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.chargedTo
                  ? `${transaction.chargedTo.user.firstName} ${transaction.chargedTo.user.lastName}`
                  : "N/A"}
              </Text>
            ),
          },
          {
            field: "transaction_id",
            name: "Transaction Id",
            filter: {
              field: "transaction.id",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.id}
              </Text>
            ),
          },
          {
            field: "product_name",
            name: "Product name",
            filter: {
              field: "product.name",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ product }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {product.name}
              </Text>
            ),
          },
          {
            field: "product_id",
            name: "Product Id",
            filter: {
              field: "product.id",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ product }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {product.id}
              </Text>
            ),
          },
          {
            field: "product_sku",
            name: "Product Sku",
            filter: {
              field: "product.sku",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ product }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {product.sku}
              </Text>
            ),
          },
          {
            field: "count",
            name: "Product Count",
            filter: {
              field: "count",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ count }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {count}
              </Text>
            ),
          },
          // {
          //   field: "categoryOption_name",
          //   name: "Revenue group",
          //   filter: {
          //     field: "categoryOption.name",
          //     type: "text",
          //   },
          //   sortable: true,
          //   cellRenderer: ({ transaction }) => (
          //     <Text weight={500} style={{ cursor: "pointer" }}>
          //       {transaction.categoryOption?.name || "N/A"}
          //     </Text>
          //   ),
          // },
          {
            field: "discountCode",
            name: "Discount code",
            // filter: {
            //   field: "discountCodes.name",
            //   type: "text",
            // },
            // sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.discountCode?.map((e) => e.name).join(", ") ||
                  "N/A"}
              </Text>
            ),
          },
          {
            field: "productSale_discount",
            name: "Discount amount",
            filter: {
              field: "discount",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ discount }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {discount || "N/A"}
              </Text>
            ),
          },
          {
            field: "transaction_subtotal",
            name: "Subtotal ",
            filter: {
              field: "transaction.subtotal",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.subtotal}
              </Text>
            ),
          },
          {
            field: "productSale_tax",
            name: "Tax",
            filter: {
              field: "tax",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ tax }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {tax}
              </Text>
            ),
          },
          {
            field: "location_name",
            name: "Location name",
            filter: {
              field: "location.name",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ location }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {location.name}
              </Text>
            ),
          },
          {
            field: "performed_by",
            name: "Performed by",
            filter: {
              field: "performed_by",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.performedBy
                  ? `${transaction.performedBy.user.firstName} ${transaction.performedBy.user.lastName}`
                  : "N/A"}
              </Text>
            ),
          },
          {
            field: "productSale_commissionValue",
            name: "Commission value",
            filter: {
              field: "commissionValue",
              type: "text",
            },
            sortable: true,
            cellRenderer: ({ commissionValue }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {commissionValue}
              </Text>
            ),
          },
          {
            field: "transaction_isRefund",
            name: "isRefund",
            filter: {
              field: "transaction.isRefund",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: ({ transaction }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {transaction.isRefund ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "productType",
            name: "Product Type",
            ...(pathname !== ROUTES.REPORT_PACKAGE && {
              filter: {
                field: "productType",
                type: "select",
                options: Object.values(ProductType),
              },
            }),
            sortable: true,
            cellRenderer: ({ productType }) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {productType}
              </Text>
            ),
          },
        ]}
        dataFetch={getReportPage}
      />
    );
  }, [filters]);

  return (
    <DashboardCard
      filters={filters}
      setFilters={setFilters}
      getDateRangeText={() => getDateRangeText(filters)}
      handleModal={() => handleModal(false)}
      handleLocChange={handleLocChange}
      isChartDetail={true}
    >
      <Button
        mb={"xs"}
        leftIcon={<MdDownload size={16} />}
        onClick={handleSaveAsCSV}
        loading={isCsvDownloading}
      >
        Download Csv
      </Button>
      <ScrollArea style={{ width: "100%" }}>
        <ShowTable />
      </ScrollArea>
    </DashboardCard>
  );
};

export default memo(POSReport);
